import type { Site, SiteStreetView as SiteStreetViewDto } from '@bk/contracts';
import { useState } from 'react';
import { useTenantQuery } from '../../../lib/useTenant';

/** Indirizzo completo di una sede per la mappa; null se non c'è abbastanza per trovarla. */
export function siteAddress(s: Site): string | null {
  const parts = [
    s.address,
    s.postal_code,
    s.municipality,
    s.province,
    s.country ?? (s.municipality ? 'Italia' : null),
  ].filter(Boolean);
  return s.address || s.municipality ? parts.join(', ') : null;
}

/**
 * Mappa Google di una sede (ADR 0022): mappa incorporata senza chiave, a partire dall'indirizzo, con il collegamento
 * per aprirla in Google Maps. L'indirizzo arriva a Google solo quando la mappa si apre.
 */
export function SiteMap({ site, open: initial = true }: { site: Site; open?: boolean }) {
  const [open, setOpen] = useState(initial);
  const address = siteAddress(site);
  if (!address)
    return (
      <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
        Manca l’indirizzo: completalo per vedere la sede sulla mappa.
      </p>
    );
  const q = encodeURIComponent(address);
  return (
    <div className="overflow-hidden rounded-lg border border-black/10">
      {open ? (
        <iframe
          title={`Mappa di ${site.name}`}
          className="block h-48 w-full"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          src={`https://maps.google.com/maps?q=${q}&hl=it&z=15&output=embed`}
        />
      ) : (
        <button
          className="flex h-24 w-full items-center justify-center bg-black/[0.03] text-sm text-accent"
          onClick={() => setOpen(true)}
        >
          Mostra sulla mappa
        </button>
      )}
      <a
        className="block border-t border-black/10 px-3 py-1.5 text-xs text-accent hover:underline"
        href={`https://www.google.com/maps/search/?api=1&query=${q}`}
        target="_blank"
        rel="noreferrer"
      >
        Apri in Google Maps · {address}
      </a>
    </div>
  );
}

/**
 * La sede vista dalla strada, a filo del suo contenitore: niente bordi, collegamenti né indirizzo, solo la vista e i
 * comandi per muoversi. Il server chiede a Google (ADR 0028) il punto dell'edificio e la foto più vicina scattata dalla
 * strada, girata verso l'edificio; se non c'è una foto resta la mappa.
 */
export function SiteStreetView({ site, className = '' }: { site: Site; className?: string }) {
  const address = siteAddress(site);
  const view = useTenantQuery<SiteStreetViewDto>(
    ['sites', site.id, 'street-view', address],
    `/sites/${site.id}/street-view`,
    { enabled: !!address },
  );
  if (!address)
    return (
      <div
        className={`flex items-center justify-center bg-black/[0.03] p-4 text-center text-xs text-ink/50 ${className}`}
      >
        Con l’indirizzo qui vedi la sede dalla strada
      </div>
    );
  if (view.isLoading) return <div className={`animate-pulse bg-black/[0.04] ${className}`} />;
  const v = view.data?.vista;
  const p = view.data?.punto;
  const src = v
    ? `https://maps.google.com/maps?layer=c&cbll=${v.lat},${v.lng}&cbp=12,${v.direzione},0,0,0&hl=it&output=svembed`
    : `https://maps.google.com/maps?q=${p ? `${p.lat},${p.lng}` : encodeURIComponent(address)}&hl=it&z=17&output=embed`;
  // Google mette in alto a sinistra un riquadro con indirizzo e collegamenti che non si può togliere: l'iframe sale
  // di CROP pixel dentro un contenitore che li taglia, e restano la vista e i comandi per muoversi.
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <iframe
        title={`${site.name} vista dalla strada`}
        className="absolute inset-x-0 block w-full border-0"
        style={{ top: -CROP, height: `calc(100% + ${CROP}px)` }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        src={src}
      />
    </div>
  );
}

/** Altezza del riquadro di Google con l'indirizzo, in cima a Street View e alla mappa incorporata. */
const CROP = 90;
